<template>
  <ag-grid-vue 
    v-bind="agChecadas" 
    ref="agChecadas" 
    id="agChecadas" 
    @grid-ready="onGridReady" 
    style="height: 65vh;"
    class="ag-theme-alpine" 
    @filterChanged="onFilterChanged" 
  />
</template>

<script>
/* eslint-disable vue/no-unused-components */
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  limitToLast,
  startAfter,
  endBefore,
  getDocs,
  getCountFromServer,
} from "firebase/firestore";
import { AgGridVue } from "ag-grid-vue";
import BaseLoader from "@/components/BaseLoader.vue";
import EmptySection from "@/components/EmptySection.vue";
import Formats from "@/mixins/Formats";
import AgGrid from "@/mixins/AgGrid";
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import moment from "moment-timezone";
import cedis from "@/data/cedis.json";
import timezones from "@/data/timezones.json";
import departments from "@/data/departments.json";
import AgBadge from "@/components/AgGrid/AgBadge.vue";
import FilterDate from "@/components/AgGrid/Filters/FilterDate.vue";
import FilterWeek from "@/components/AgGrid/Filters/FilterWeek.vue";
import FFWeek from "@/components/AgGrid/Filters/FFWeek.vue";
export default {
  name: 'Index',
  mixins: [AgGrid, Formats],
  components: {
    AgGridVue,
    BaseLoader,
    EmptySection,
    AgBadge,
    FilterWeek,
    FFWeek,
    agDateInput: FilterDate,
  },
  data() {
    return {
      firstVisible: null,
      lastVisible: null,
      lastStartRow: 0,
      employees: null,
      agChecadas: {
        columnDefs: [
          {
            headerName: "ID Empleado",
            field: "employee_id",
            filter: "agTextColumnFilter",
            filterParams: {
              filterOptions: ["startsWith", "equals", "notEqual"],
            },
            width: 130,
            suppressSizeToFit: true,
          },
          {
            headerName: "Nombre",
            field: "name",
            width: 300,
            suppressSizeToFit: true,
            filter: "agTextColumnFilter",
            filterParams: {
              filterOptions: ["startsWith"],
            },
            valueGetter: ({ data }) => {
              if(data.name){
                return data.name;
              } else {
                const employee = this.employees.find((emp) => emp.id === data.employee_id);
                return employee ? employee.name : "";
              }
            },
          },
          {
            headerName: "CEDIS",
            filter: "agSetColumnFilter",
            filterParams: {
              values: cedis.map((x) => x.id),
              defaultToNothingSelected: true,
            },
            field: "cedis",
            valueGetter: ({ data }) => {
              if(data.cedis){
                return data.cedis;
              } else {
                const employee = this.employees.find((emp) => emp.id === data.employee_id);
                return employee ? employee.cedis : "";
              }
            },
            cellRendererSelector: ({ value }) => {
              const cedisV = cedis.find(x => x.id === value);
              if (!cedisV) return null;
              return {
                component: "AgBadge",
                params: {
                  text: cedisV.id,
                  color: cedisV.color,
                }
              };
            },
          },
          {
            headerName: "Turno",
            field: "turno",
            filter: "agSetColumnFilter",
            filterParams: {
              values: (params) => {
                const db = getFirestore();
                const coll = collection(db, "work_shifts");
                getDocs(coll).then((snapshot) => {
                  const ws = snapshot.docs.map((doc) => doc.data().name);
                  params.success(ws);
                });
              },
              suppressMiniFilter: true,
              suppressSorting: true,
              defaultToNothingSelected: true,
            },
          },
          {
            headerName: "Departamento",
            field: "departamento",
            filter: "agSetColumnFilter",
            filterParams: {
              values: departments.map((x) => x.name),
              defaultToNothingSelected: true,
            },
            width: 200,
            suppressSizeToFit: true,
            valueGetter: ({ data }) => {
              if(data.departamento){
                return data.departamento;
              } else {
                const employee = this.employees.find((emp) => emp.id === data.employee_id);
                return employee ? employee.departamento : null;
              }
            },
          },
          {
            headerName: "Fecha",
            field: "date",
            filter: "agDateColumnFilter",
            filterParams: {
              filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
            },
            valueFormatter: (params) => this.dateFormatter(params, "YYYY-MM-DD"),
          },
          {
            headerName: "Semana",
            field: "date",
            filter: "FilterWeek",
            floatingFilterComponent: "FFWeek",
            valueFormatter: (params) => this.dateFormatter(params, "GGGG-WW"),
          },
          {
            headerName: "Entrada",
            field: "inicio_jornada_timestamp",
            valueFormatter: (params) => this.dateFormatter(params, "HH:mm:ss"),
            tooltipValueGetter: this.getTimezone,

          },
          {
            headerName: "Descanso 1",
            field: "inicio_descanso_timestamp",
            valueFormatter: (params) => this.dateFormatter(params, "HH:mm:ss"),
            tooltipValueGetter: this.getTimezone,
          },
          {
            headerName: "Regreso 1",
            field: "fin_descanso_timestamp",
            valueFormatter: (params) => this.dateFormatter(params, "HH:mm:ss"),
            tooltipValueGetter: this.getTimezone,
          },
          {
            headerName: "Descanso 2",
            field: "inicio_descanso_2_timestamp",
            valueFormatter: (params) => this.dateFormatter(params, "HH:mm:ss"),
            tooltipValueGetter: this.getTimezone,
          },
          {
            headerName: "Regreso 2",
            field: "fin_descanso_2_timestamp",
            valueFormatter: (params) => this.dateFormatter(params, "HH:mm:ss"),
            tooltipValueGetter: this.getTimezone,
          },
          {
            headerName: "Salida",
            field: "fin_jornada_timestamp",
            valueFormatter: (params) => this.dateFormatter(params, "HH:mm:ss"),
            tooltipValueGetter: this.getTimezone,
          },
        ],
        defaultColDef: {
          suppressHeaderMenuButton: true,
          // menuTabs: ["filterMenuTab"],
          sortable: false,
          resizable: true,
          cellDataType: false,
          filter: false,
          filterParams: {
            maxNumConditions: 1,
          },
          floatingFilter: true,
        },
        autoSizeStrategy: {
          type: 'fitGridWidth',
          defaultMinWidth: 100,
        },
        rowModelType: "serverSide",
        rowSelection: "single",
        pagination: true,
        paginationPageSize: 2000,
        paginationPageSizeSelector: [2000],
        cacheBlockSize: 2000,
        maxBlocksInCache: 1,
        loadingOverlayComponent: "BaseLoader",
        noRowsOverlayComponent: "EmptySection",
        suppressCellFocus: true,
        enableCellTextSelection: true,
        alwaysShowVerticalScroll: true,
        localeText: AG_GRID_LOCALE_ES,
        tooltipShowDelay: 3000,
      },
    }
  },
  methods: {
    onGridReady({ api }) {
      if (!this.employees) {
        const db = getFirestore();
        const coll = collection(db, "employees")
        getDocs(coll).then((snapshot) => {
          this.employees = snapshot.docs.map((doc) => {
            const { id, name, cedis, departamento } = doc.data();
            return { id, name, cedis, departamento };
          });
          api.setGridOption("serverSideDatasource", this.getServerSideDatasource());
        });
      } else {
        api.setGridOption("serverSideDatasource", this.getServerSideDatasource());
      }
    },
    dateFormatter(params, format) {
      const { value, data } = params;
      if (!value || !value.seconds) return null;
      let cedisCheck;
      if(data.cedis){
        cedisCheck = data.cedis;
      } else {
        cedisCheck = this.employees.find((emp) => emp.id === data.employee_id).cedis;
      }
      return moment.unix(value.seconds).tz(timezones[cedisCheck]).format(format);
    },
    getTimezone({ data, value }) {
      if (!value || !value.seconds || !data.cedis) return null;
      return `Hora de ${data.cedis}`;
    },
    getServerSideDatasource() {
      return {
        getRows: (params) => {
          const { filterModel, endRow, startRow } = params.request;

          const db = getFirestore();
          const coll = collection(db, "checador");
          let qry = query(coll);
          let qryCount = query(coll);

          // FILTERS
          Object.entries(filterModel).forEach(([colKey, obj]) => {
            if (colKey === "date_1") {
              colKey = "date";
            }
            if(colKey === "name" ){
              obj.filter = obj.filter.toUpperCase();
            }
            this.getConditions(obj, colKey).forEach((condition) => {
              qry = query(qry, where(...condition));
              qryCount = query(qryCount, where(...condition));
            })
          });

          // SORTING
          qry = query(qry, orderBy("date", "desc"));

          // PAGINATION
          const pageSize = endRow - startRow;
          if (startRow === 0) { // First load
            qry = query(qry, limit(pageSize));
          } else if (this.lastStartRow > startRow) { // PREVIOUS
            qry = query(qry, endBefore(this.firstVisible), limitToLast(pageSize));
          } else { // NEXT
            qry = query(qry, startAfter(this.lastVisible), limit(pageSize));
          }


          Promise.all([
            getCountFromServer(qryCount),
            getDocs(qry)
          ])
            .then(([countSnapshot, querySnapshot]) => {
              const count = countSnapshot.data().count;
              if (count > 0 && !querySnapshot.empty) {
                const rowData = querySnapshot.docs.map((doc) => {
                  return { firebaseId: doc.id, ...doc.data() };
                });

                params.success({ rowData, rowCount: count });

                const isFirstPage = startRow === 0;
                const isLastPage = endRow >= count;

                if (isFirstPage) {
                  this.firstVisible = null;
                  this.lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
                } else if (isLastPage) {
                  this.firstVisible = querySnapshot.docs[0];
                  this.lastVisible = null;
                } else {
                  this.firstVisible = querySnapshot.docs[0];
                  this.lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
                }

              } else {
                params.success({ rowData: [], rowCount: 0 });
              }
              this.lastStartRow = startRow;

            })
        },
      };
    },
    onFilterChanged({ api, columns }) {
      const { colId } = columns[0];
      const filterModel = api.getFilterModel();

      if (Object.keys(filterModel).length > 1) {
        api.setFilterModel({
          [colId]: filterModel[colId]
        });
      }
    },
  },
}
</script>

<style>
#agChecadas {
  .ag-paging-button[ref="btFirst"],
  .ag-paging-button[ref="btLast"] {
    display: none !important;
  }
}
</style>